import { useCallback, useEffect, useState } from 'react';

/**
 * Ajuste une boîte de ratio `aspect` dans une zone `areaW × areaH`, au plus grand,
 * centrée. La boîte épouse le contenu (largeur- ou hauteur-limitée) : le média placé
 * dedans en `object-contain` la remplit pile, donc l'arrondi colle et l'overscan
 * anti-liseré (clippé par la boîte) reste efficace même hors format 16:9.
 */
export function fitBox(
  areaW: number,
  areaH: number,
  aspect: number,
): { width: number; height: number } | null {
  if (areaW <= 0 || areaH <= 0 || aspect <= 0) return null;
  const areaAspect = areaW / areaH;
  return aspect >= areaAspect
    ? { width: Math.floor(areaW), height: Math.floor(areaW / aspect) }
    : { width: Math.floor(areaH * aspect), height: Math.floor(areaH) };
}

/**
 * Mesure la taille d'un élément (via `ResizeObserver`) pour alimenter `fitBox`.
 * Callback ref : ré-attache l'observer quand le nœud apparaît/change, même si la zone
 * n'est montée qu'après le composant (ex. le partage démarre en cours de live).
 */
export function useAreaSize<T extends HTMLElement>() {
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [node, setNode] = useState<T | null>(null);
  const ref = useCallback((el: T | null) => setNode(el), []);

  useEffect(() => {
    if (!node) return;
    const measure = () => setSize({ w: node.clientWidth, h: node.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return { ref, size };
}
